import { AxiosError, Method } from 'axios';
import Axios from '@/utils/axios';
import { ApiKeyRow } from '@/services/api/apiKeys';
import { WebhookEndpointRow } from '@/services/api/webhooks';

// ─── Sandbox simulator (real API) ─────────────────────────────────────
// The Developer page's Sandbox tab drives REAL sandbox shipments on the
// logistic integration surface, with the portal's own login. `X-Api-Mode:
// sandbox` is what makes it sandbox: what it creates is a sandbox shipment,
// reads see sandbox shipments only, and every simulated step sends a real
// webhook to the client's Sandbox (TEST) endpoint. Nothing reaches a hub, a
// rider or delivery-service.
//   POST /v1/integration/shipments               create
//   GET  /v1/integration/shipments               list (sandbox only)
//   GET  /v1/integration/shipments/:id           detail
//   POST /v1/integration/shipments/:id/cancel    cancel (CREATED only)
//   POST /v1/integration/shipments/:id/simulate  next status
//   GET  /v1/integration/shipments/:id/webhook-deliveries  the webhooks sent
// Backend: nest-logistic-service docs/shipment/SHIPMENT-INTEGRATION-ENDPOINTS.md §5–§6.
const SANDBOX_HEADERS = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'sandbox',
} as const;

const BASE = '/v1/integration/shipments';

export type SandboxShipmentStatus =
  | 'CREATED'
  | 'AT_HUB'
  | 'PARTIALLY_DISPATCHED'
  | 'DISPATCHED'
  | 'IN_DELIVERY'
  | 'IN_DISPUTE'
  | 'COMPLETED'
  | 'RETURNED'
  | 'CANCELLED'
  | 'FAILED';

/**
 * Where the simulator can take a shipment next — the first entry is what a
 * plain "Next" does, the rest are branches. Mirror of the backend's
 * `SANDBOX_NEXT_STATUSES`; the simulate response's `nextStatuses` is the
 * authority, this only labels the buttons of rows loaded from the list.
 */
export const SANDBOX_NEXT_STATUSES: Partial<
  Record<SandboxShipmentStatus, SandboxShipmentStatus[]>
> = {
  CREATED: ['AT_HUB'],
  AT_HUB: ['DISPATCHED'],
  DISPATCHED: ['IN_DELIVERY'],
  IN_DELIVERY: ['COMPLETED', 'FAILED'],
  FAILED: ['IN_DELIVERY', 'RETURNED'],
};

interface SandboxContact {
  name: string;
  phone: string | null;
}

interface SandboxLocation {
  address: string;
  latitude: number;
  longitude: number;
  notes: string | null;
}

/** A list row — the integration shipment head. */
export interface SandboxShipmentRow {
  id: string;
  waybill: string;
  bookingId: string;
  status: SandboxShipmentStatus;
  type: string;
  serviceType: string;
  courier: { name: string; code: string | null } | null;
  sender: SandboxContact;
  recipient: SandboxContact;
  origin: SandboxLocation | null;
  destination: SandboxLocation;
  totalWeight: number;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SandboxShipmentItem {
  id: string;
  name: string;
  quantity: number;
  weight: number;
  status: string;
}

/** One proof photo of a timeline event — the same object as the webhook's
 *  `event.media[]`. */
export interface SandboxEventMedia {
  id: string;
  type: string;
  url: string;
  createdAt: string;
}

export interface SandboxTimelineEvent {
  id: string;
  type: string;
  status: SandboxShipmentStatus;
  message: string;
  hubName: string | null;
  reason: { code: string | null; message: string | null } | null;
  /** Proof photos this event carries (`[]` when none). */
  media?: SandboxEventMedia[];
  createdBy: string | null;
  occurredAt: string;
}

/** `GET /v1/integration/shipments/:id` — the fields this page shows. */
export interface SandboxShipmentDetail extends SandboxShipmentRow {
  items: SandboxShipmentItem[];
  /** Newest first. */
  timeline: SandboxTimelineEvent[];
}

export type WebhookDeliveryStatus =
  'PENDING' | 'PROCESSING' | 'DONE' | 'FAILED' | 'DEAD' | 'SKIPPED';

/** One webhook as sent to one endpoint (`webhook-deliveries`). */
export interface SandboxWebhookDelivery {
  eventId: string;
  eventType: string;
  sequence: number;
  eventStatus: string | null;
  occurredAt: string | null;
  endpoint: {
    id: string;
    url: string;
    environment: 'LIVE' | 'TEST';
    disabled: boolean;
  } | null;
  delivery: {
    status: WebhookDeliveryStatus;
    attempts: number;
    lastError: string | null;
    nextRetryAt: string | null;
    deliveredAt: string | null;
  };
  createdAt: string;
  payload: unknown;
}

/** One HTTP round trip, as the request/response viewer shows it. */
export interface SandboxExchange {
  method: string;
  path: string;
  /** The JSON body sent; undefined for a request without one. */
  body?: unknown;
  status: number;
  response: unknown;
}

const send = async (
  method: Method,
  url: string,
  body?: unknown
): Promise<SandboxExchange> => {
  const sent = { method: method.toUpperCase(), path: url, body };
  try {
    const res = await Axios.request({
      method,
      url,
      data: body,
      headers: SANDBOX_HEADERS,
    });
    return { ...sent, status: res.status, response: res.data };
  } catch (err) {
    // An HTTP answer (4xx/5xx) is part of the lesson — show it. Only a
    // request that never got one (network down) is an error here.
    const response = (err as AxiosError).response;
    if (response) {
      return { ...sent, status: response.status, response: response.data };
    }
    throw err;
  }
};

// ─── The two ways to read one request ─────────────────────────────────
// This page authenticates with the portal login and picks sandbox with
// `X-Api-Mode` — the portal never holds the plaintext key (it is shown once,
// at creation). A partner's own system makes the SAME call with its
// dash_test_ key instead, and needs no X-Api-Mode: the key's environment
// decides. The viewer shows both, so neither is mistaken for the other.

/** Where partner systems call the API (the API docs' base URL). */
export const PUBLIC_API_BASE = 'https://api.dashelectric.co';

/** A dash_test_ key is its 18-char prefix + 24 more random chars. */
const KEY_TAIL_LENGTH = 24;

/** The newest active Sandbox key's display prefix — what the system view
 *  shows (the rest is masked; nobody can show the full key again). */
export const activeSandboxKeyPrefix = (
  keys: ApiKeyRow[],
  now: number = Date.now()
): string | null =>
  keys
    .filter(
      (k) =>
        k.environment === 'TEST' &&
        !k.revokedAt &&
        (!k.expiresAt || Date.parse(k.expiresAt) > now)
    )
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0]
    ?.keyPrefix ?? null;

const bodyLines = (body: unknown) =>
  body !== undefined ? ['', JSON.stringify(body, null, 2)] : [];

/** What this page actually sent. */
export const describeSentRequest = (ex: SandboxExchange) =>
  [
    `${ex.method} ${ex.path}`,
    'Authorization: Bearer ••••••  (your portal login)',
    'X-Api-Mode: sandbox',
    ...bodyLines(ex.body),
  ].join('\n');

/** The same call as the partner's own system makes it. */
export const describeSystemRequest = (
  ex: SandboxExchange,
  keyPrefix: string | null
) =>
  [
    `${ex.method} ${ex.path}`,
    `x-api-key: ${
      keyPrefix
        ? `${keyPrefix}${'•'.repeat(KEY_TAIL_LENGTH)}`
        : '<your dash_test_ key>'
    }`,
    ...bodyLines(ex.body),
  ].join('\n');

/** The system call as a pasteable cURL. The key is an env var: the masked
 *  value would not run, and the real one never leaves its owner. */
export const toCurl = (ex: SandboxExchange) => {
  const lines = [
    `curl -X ${ex.method} "${PUBLIC_API_BASE}${ex.path}"`,
    `  -H "x-api-key: $DASH_TEST_KEY"`,
  ];
  if (ex.body !== undefined) {
    // Single-quoted for the shell: close, escaped quote, reopen.
    const json = JSON.stringify(ex.body).replace(/'/g, "'\\''");
    lines.push(`  -H "Content-Type: application/json"`, `  -d '${json}'`);
  }
  return lines.join(' \\\n');
};

export const createSandboxShipment = (body: Record<string, unknown>) =>
  send('post', BASE, body);

export const getSandboxShipment = (id: string) => send('get', `${BASE}/${id}`);

export const fetchSandboxWebhookDeliveries = async (
  id: string
): Promise<SandboxWebhookDelivery[]> => {
  const res = await Axios.get<{ data: { data: SandboxWebhookDelivery[] } }>(
    `${BASE}/${id}/webhook-deliveries`,
    { headers: SANDBOX_HEADERS }
  );
  return res.data.data.data;
};

/** Still on its way: the list keeps polling while any delivery is here. */
export const isDeliveryInFlight = (d: SandboxWebhookDelivery) =>
  d.delivery.status === 'PENDING' || d.delivery.status === 'PROCESSING';

export const cancelSandboxShipment = (id: string, reason: string) =>
  send('post', `${BASE}/${id}/cancel`, { reason });

/**
 * Resends the shipment's latest webhook now; the answer is its
 * webhook-deliveries row after the attempt. No body. The API refuses a
 * delivered / in-flight / out-of-date one (409), no active endpoint (400) and
 * a retry within 30 s (429).
 */
export const retrySandboxWebhook = (id: string) =>
  send('post', `${BASE}/${id}/webhook/retry`);

/** `status` omitted = the happy-path next step; `reasonCode` is FAILED only. */
export const simulateSandboxShipment = (
  id: string,
  status?: SandboxShipmentStatus,
  reasonCode?: string
) =>
  send(
    'post',
    `${BASE}/${id}/simulate`,
    status ? { status, ...(reasonCode ? { reasonCode } : {}) } : {}
  );

export interface SandboxShipmentListQuery {
  page: number;
  limit: number;
  status?: SandboxShipmentStatus;
  search?: string;
}

export interface SandboxShipmentPage {
  rows: SandboxShipmentRow[];
  total: number;
}

export const listSandboxShipments = async (
  query: SandboxShipmentListQuery = { page: 1, limit: 20 }
): Promise<SandboxShipmentPage> => {
  const res = await Axios.get<{
    data: { data: SandboxShipmentRow[]; pagination: { total: number } };
  }>(BASE, {
    params: {
      page: query.page,
      limit: query.limit,
      ...(query.status ? { status: query.status } : {}),
      ...(query.search ? { search: query.search } : {}),
    },
    headers: SANDBOX_HEADERS,
  });
  return {
    rows: res.data.data.data,
    total: res.data.data.pagination?.total ?? res.data.data.data.length,
  };
};

// ─── Readiness ────────────────────────────────────────────────────────
// The simulator sends real webhooks and is meant to be driven with a
// dash_test_ key, so the backend refuses it until the client has an active
// Sandbox key AND an active Sandbox webhook endpoint. The tab checks the same
// thing up front so the buttons can say why they are off instead of failing
// on click.

export interface SandboxReadiness {
  hasKey: boolean;
  hasEndpoint: boolean;
  ready: boolean;
  /** Why it is not ready, worded like the backend's error; null when ready. */
  message: string | null;
}

export const sandboxReadiness = (
  keys: ApiKeyRow[],
  endpoints: WebhookEndpointRow[],
  now: number = Date.now()
): SandboxReadiness => {
  const hasKey = keys.some(
    (k) =>
      k.environment === 'TEST' &&
      !k.revokedAt &&
      (!k.expiresAt || Date.parse(k.expiresAt) > now)
  );
  const hasEndpoint = endpoints.some(
    (e) => e.environment === 'TEST' && !e.disabledAt
  );
  let message: string | null = null;
  if (!hasKey && !hasEndpoint) {
    message =
      'Simulation needs an active Sandbox API key and an active Sandbox webhook endpoint — you have neither yet. Create both, then come back.';
  } else if (!hasKey) {
    message =
      'Simulation needs an active Sandbox API key — yours is missing, revoked or expired. Create one on the API keys tab.';
  } else if (!hasEndpoint) {
    message =
      'Simulation sends real webhooks to your Sandbox endpoint, and you have none active. Add one on the Webhooks tab.';
  }
  return { hasKey, hasEndpoint, ready: message === null, message };
};

// ─── Sandbox outbound orders (warehouse) ──────────────────────────────
// The same client API a partner calls for warehouse orders, in sandbox mode
// (nest-logistic-service docs/warehouse/wms-client-api-outbound-orders.md):
//   POST /v1/integration/outbound-orders              create
//   GET  /v1/integration/outbound-orders              list (sandbox only)
//   GET  /v1/integration/outbound-orders/:id          detail
//   POST /v1/integration/outbound-orders/:id/cancel   cancel (before shipping)
//   POST /v1/integration/outbound-orders/:id/simulate next status (sandbox)
// Sandbox orders go to Dash Sandbox Hub, where every catalog SKU holds 100
// units; no warehouse ever processes them — the simulator moves them as if
// the warehouse and the courier had — and no order webhooks exist yet.
const ORDERS_BASE = '/v1/integration/outbound-orders';

/** Every SKU of the client's catalog holds this many units in Sandbox. */
export const SANDBOX_ORDER_STOCK_PER_SKU = 100;

export type SandboxOrderStatus =
  | 'OPEN'
  | 'PREPARING'
  | 'READY_TO_PICKUP'
  | 'SHIPPED'
  | 'IN_DELIVERY'
  | 'DELIVERED'
  | 'FAILED'
  | 'CANCELLED';

/** The redacted order the client API returns (create / get / list / cancel). */
export interface SandboxOrder {
  id: string;
  code: string;
  externalID: string;
  status: SandboxOrderStatus;
  hub: { id: number; name: string; code: string | null };
  serviceType: 'SAME_DAY' | 'NEXT_DAY';
  orderDate: string;
  expectedDeliveryDate: string;
  recipient: { name: string; phone: string };
  destination: {
    address: string;
    latitude: number;
    longitude: number;
    notes: string;
    /** The city Dash resolved from the address / point (its city master);
     *  both null while unresolved. Absent on older fixtures. */
    cityID?: number | null;
    city?: string | null;
  };
  lines: { sku: string; itemName: string; uomName: string; qty: number }[];
  notes: string;
  fulfillment: {
    carrier: string | null;
    status: string | null;
    waybill: string | null;
    shipmentID: string | null;
  } | null;
  cancellation: { reason: string; cancelledAt: string } | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Where the simulator can take an order next — the first entry is what a
 * plain "Next" does, the rest are branches. Mirror of the backend's
 * `SANDBOX_ORDER_NEXT_STATUSES`; the simulate response's `nextStatuses` is the
 * authority, this labels the buttons of an order read with GET.
 */
export const SANDBOX_ORDER_NEXT_STATUSES: Partial<
  Record<SandboxOrderStatus, SandboxOrderStatus[]>
> = {
  OPEN: ['PREPARING'],
  PREPARING: ['READY_TO_PICKUP'],
  READY_TO_PICKUP: ['SHIPPED'],
  SHIPPED: ['IN_DELIVERY'],
  IN_DELIVERY: ['DELIVERED', 'FAILED'],
};

/** The happy path, in order — the detail page's status track. */
export const SANDBOX_ORDER_PATH: SandboxOrderStatus[] = [
  'OPEN',
  'PREPARING',
  'READY_TO_PICKUP',
  'SHIPPED',
  'IN_DELIVERY',
  'DELIVERED',
];

export const createSandboxOrder = (body: Record<string, unknown>) =>
  send('post', ORDERS_BASE, body);

export const getSandboxOrder = (id: string) =>
  send('get', `${ORDERS_BASE}/${id}`);

export const cancelSandboxOrder = (id: string, reason: string) =>
  send('post', `${ORDERS_BASE}/${id}/cancel`, reason ? { reason } : {});

/** `status` omitted = the happy-path next step; FAILED only from IN_DELIVERY.
 *  The response `data` is `{ previousStatus, status, nextStatuses, order }`. */
export const simulateSandboxOrder = (id: string, status?: SandboxOrderStatus) =>
  send('post', `${ORDERS_BASE}/${id}/simulate`, status ? { status } : {});

export interface SandboxOrderListQuery {
  page: number;
  limit: number;
  status?: SandboxOrderStatus;
  search?: string;
}

export const listSandboxOrders = async (
  query: SandboxOrderListQuery = { page: 1, limit: 20 }
): Promise<{ rows: SandboxOrder[]; total: number }> => {
  const res = await Axios.get<{
    data: { data: SandboxOrder[]; pagination: { total: number } };
  }>(ORDERS_BASE, {
    params: {
      page: query.page,
      limit: query.limit,
      ...(query.status ? { status: query.status } : {}),
      ...(query.search ? { search: query.search } : {}),
    },
    headers: SANDBOX_HEADERS,
  });
  return {
    rows: res.data.data.data,
    total: res.data.data.pagination?.total ?? res.data.data.data.length,
  };
};
